// OOXML numbering (word/numbering.xml).
//
// Word stores list definitions in two tables:
//   <w:abstractNum w:abstractNumId="N">  — the reusable definition; one
//       <w:lvl w:ilvl="k"> per indent level, each with a <w:numFmt> (decimal,
//       lowerRoman, bullet, …), a <w:lvlText> (the glyph/pattern) and <w:start>.
//   <w:num w:numId="M">  — a concrete instance pointing at an abstractNumId,
//       optionally with <w:lvlOverride>/<w:startOverride> tweaks.
// A paragraph's <w:numPr> carries the numId + ilvl; we resolve that pair to a
// Plate listStyleType + start value, and — via createNumberingCounter — to
// Word's own running ordinal for that paragraph.
import { attrByLocalName, childByLocalName, childrenByLocalName, val } from './readDocxXml';
import { BULLET_GLYPH_TO_LIST_STYLE, DEFAULT_LIST_START, MAX_LIST_START, NUM_FMT_TO_LIST_STYLE, OOXML_LIST_STYLE_TYPES } from './constants';
import type { ResolvedNumberingLevel } from './types';

// OOXML bounds ilvl to 0-8; a hostile numbering.xml must not be trusted to
// respect that, or an attacker-controlled ilvl becomes an unbounded Map key
// axis in createNumberingCounter's per-(numId, ilvl) state.
const MIN_ILVL = 0;
const MAX_ILVL = 8;

// <w:lvlRestart w:val="0"/> — this level's counter never resets when a
// shallower level advances (kept per O1 in the Plan Review Log: implementing
// default reset semantics without this produces silently wrong ordinals).
const LVL_RESTART_NEVER = 0;

function isValidIlvl(ilvl: number): boolean {
    return Number.isInteger(ilvl) && ilvl >= MIN_ILVL && ilvl <= MAX_ILVL;
}

// Clamp a parsed start/ordinal to a finite integer in [1, MAX_LIST_START] so a
// hostile or malformed value (huge, negative, NaN, "abc") can never reach
// counter-reset / <ol start> in the rendered document (§5).
function clampListStart(value: number | undefined): number | undefined {
    if (value == null || !Number.isFinite(value)) return undefined;
    return Math.min(MAX_LIST_START, Math.max(1, Math.round(value)));
}

interface LevelDef {
    numFmt?: string;
    lvlText?: string;
    start?: number;
    /** True when this level's own <w:lvlRestart val="0"/> suppresses resets. */
    neverRestarts?: boolean;
}

export interface NumberingData {
    numToAbstract: Map<string, string>;
    abstractLevels: Map<string, Map<number, LevelDef>>;
    numOverrides: Map<string, Map<number, LevelDef>>;
}

function readLevel(lvlEl: Element): { ilvl: number; def: LevelDef } | null {
    const ilvlRaw = attrByLocalName(lvlEl, 'ilvl');
    const ilvl = ilvlRaw != null ? parseInt(ilvlRaw, 10) : NaN;
    if (!isValidIlvl(ilvl)) return null;
    const startRaw = val(childByLocalName(lvlEl, 'start'));
    const start = startRaw != null ? clampListStart(parseInt(startRaw, 10)) : undefined;
    const lvlRestart = val(childByLocalName(lvlEl, 'lvlRestart'));
    return {
        ilvl,
        def: {
            numFmt: val(childByLocalName(lvlEl, 'numFmt')) ?? undefined,
            lvlText: val(childByLocalName(lvlEl, 'lvlText')) ?? undefined,
            start,
            neverRestarts: lvlRestart != null && parseInt(lvlRestart, 10) === LVL_RESTART_NEVER,
        },
    };
}

export function parseNumbering(numberingDoc: Document | null): NumberingData {
    const numToAbstract = new Map<string, string>();
    const abstractLevels = new Map<string, Map<number, LevelDef>>();
    const numOverrides = new Map<string, Map<number, LevelDef>>();

    const root = numberingDoc?.documentElement ?? null;
    if (!root) return { numToAbstract, abstractLevels, numOverrides };

    for (const abstractNum of childrenByLocalName(root, 'abstractNum')) {
        const id = attrByLocalName(abstractNum, 'abstractNumId');
        if (id == null) continue;
        const levels = new Map<number, LevelDef>();
        for (const lvl of childrenByLocalName(abstractNum, 'lvl')) {
            const read = readLevel(lvl);
            if (read) levels.set(read.ilvl, read.def);
        }
        abstractLevels.set(id, levels);
    }

    for (const num of childrenByLocalName(root, 'num')) {
        const numId = attrByLocalName(num, 'numId');
        if (numId == null) continue;
        const abstractId = val(childByLocalName(num, 'abstractNumId'));
        if (abstractId != null) numToAbstract.set(numId, abstractId);

        const overrides = new Map<number, LevelDef>();
        for (const lvlOverride of childrenByLocalName(num, 'lvlOverride')) {
            const ilvlRaw = attrByLocalName(lvlOverride, 'ilvl');
            const ilvl = ilvlRaw != null ? parseInt(ilvlRaw, 10) : NaN;
            if (!isValidIlvl(ilvl)) continue;
            const startOverride = val(childByLocalName(lvlOverride, 'startOverride'));
            const nestedLvl = childByLocalName(lvlOverride, 'lvl');
            const nested = nestedLvl ? readLevel(nestedLvl)?.def : undefined;
            const start = startOverride != null ? clampListStart(parseInt(startOverride, 10)) : nested?.start;
            overrides.set(ilvl, {
                numFmt: nested?.numFmt,
                lvlText: nested?.lvlText,
                start,
                neverRestarts: nested?.neverRestarts,
            });
        }
        if (overrides.size > 0) numOverrides.set(numId, overrides);
    }

    return { numToAbstract, abstractLevels, numOverrides };
}

// Legal/outline numbering (Word's "Legal" list style: 1, 1.1, 1.1.1, …): a
// deeper level's own lvlText composes MULTIPLE ancestor placeholders (e.g.
// "%1.%2." at ilvl 1), unlike a plain numbered level's single "%1.". Flags
// such a level as the `decimal-legal` listStyleType at import; BlockList
// renders the faithful composite marker ("2.1.3.") from the flat indent
// model by walking ancestor siblings by depth (computeLegalMarkerChain /
// computeLegalMarkerChainsForSiblings, RECUR-27726), and plateToHtml emits
// the same chain as literal leading text for email/comment output.
function hasCompositeLvlText(lvlText: string | undefined): boolean {
    if (!lvlText) return false;
    return new Set(lvlText.match(/%[1-9]/g)).size >= 2;
}

function numFmtToListStyle(numFmt: string | undefined, lvlText: string | undefined): string {
    if (numFmt === 'bullet') {
        const glyph = (lvlText ?? '').trim().charAt(0);
        return BULLET_GLYPH_TO_LIST_STYLE[glyph] ?? OOXML_LIST_STYLE_TYPES.DISC;
    }
    if (hasCompositeLvlText(lvlText)) {
        return OOXML_LIST_STYLE_TYPES.DECIMAL_LEGAL;
    }
    // Prototype-safe lookup (§5): a plain object literal returns its inherited
    // Object.prototype member for a key like "__proto__", which is truthy and
    // would defeat the `?? DECIMAL` fallback below, leaking a non-string value
    // into listStyleType.
    if (numFmt && Object.prototype.hasOwnProperty.call(NUM_FMT_TO_LIST_STYLE, numFmt)) {
        return NUM_FMT_TO_LIST_STYLE[numFmt];
    }
    return OOXML_LIST_STYLE_TYPES.DECIMAL;
}

export function resolveNumberingLevel(numId: string, ilvl: number, data: NumberingData): ResolvedNumberingLevel | null {
    const abstractId = data.numToAbstract.get(numId);
    if (abstractId == null) return null;

    const base = data.abstractLevels.get(abstractId)?.get(ilvl);
    const override = data.numOverrides.get(numId)?.get(ilvl);

    const numFmt = override?.numFmt ?? base?.numFmt;
    if (numFmt == null) return null;

    const lvlText = override?.lvlText ?? base?.lvlText;
    const start = clampListStart(override?.start ?? base?.start) ?? DEFAULT_LIST_START;

    return {
        listStyleType: numFmtToListStyle(numFmt, lvlText),
        start,
        ordered: numFmt !== 'bullet',
    };
}

function levelNeverRestarts(numId: string, ilvl: number, data: NumberingData): boolean {
    const abstractId = data.numToAbstract.get(numId);
    const override = data.numOverrides.get(numId)?.get(ilvl);
    const base = abstractId != null ? data.abstractLevels.get(abstractId)?.get(ilvl) : undefined;
    return !!(override?.neverRestarts ?? base?.neverRestarts);
}

export interface NumberingCounter {
    /** Word's next running ordinal for this (numId, ilvl), in document order. */
    next(numId: string, ilvl: number): number;
}

/**
 * A running counter per (numId, ilvl), seeded from each level's resolved
 * `start` (which already includes startOverride) and advanced once per
 * numbered paragraph encountered, in document order.
 *
 * Word's default level-restart semantics: a level's counter resets to its
 * seeded start whenever any SHALLOWER level in the SAME numId advances,
 * unless that level's own <w:lvlRestart val="0"/> suppresses the reset
 * (§2.7 assumption 1 — w:numStyleLink / w:isLgl / w:suff are not handled).
 */
export function createNumberingCounter(data: NumberingData): NumberingCounter {
    const state = new Map<string, Map<number, number>>();

    return {
        next(numId: string, ilvl: number): number {
            let levels = state.get(numId);
            if (!levels) {
                levels = new Map<number, number>();
                state.set(numId, levels);
            }

            const seed = resolveNumberingLevel(numId, ilvl, data)?.start ?? DEFAULT_LIST_START;
            const current = levels.get(ilvl);
            const advanced = clampListStart(current == null ? seed : current + 1) ?? seed;
            levels.set(ilvl, advanced);

            // This level just advanced — reset every DEEPER level's counter
            // unless that deeper level opted out via w:lvlRestart="0".
            for (const deeper of Array.from(levels.keys())) {
                if (deeper <= ilvl) continue;
                if (levelNeverRestarts(numId, deeper, data)) continue;
                levels.delete(deeper);
            }

            return advanced;
        },
    };
}
